import { sharedCardCount } from 'components/Table/HoldEmCards';

import { dealtCards } from './playByPlayRounds';
import { CardRef } from './types';

/** The most boards a run-out deals (shared/config/multiRunOut.js MAX_RUNS). */
const MAX_RUNS = 3;

/**
 * Whether `runs` describes a run-out in progress, "<total>,<stage>,<current>".
 * The client's copy of `isMultiRunOut` (shared/config/multiRunOut.js), which
 * decides on the server which boards the felt is sent: a vote ("v…") or a
 * malformed row is not a run-out.
 */
export const isLiveRunOut = (runs: unknown): boolean => {
  const parts = String(runs ?? '').split(',');
  const total = Number(parts[0]);
  const current = Number(parts[2]);
  return (
    parts.length === 3 &&
    parts.every((part) => /^\d+$/.test(part)) &&
    total >= 2 &&
    total <= MAX_RUNS &&
    current >= 1 &&
    current <= total
  );
};

/**
 * Every board of a run-out on the felt, run 1 first, as far as each is dealt;
 * undefined when the hand is not running more than once.
 *
 * `multiRunCards` holds the board being settled in full and every other run as
 * its divergent tail (cash-game-broadcast, the same shape HoldEmCards draws the
 * fork from), so each tail is completed with the shared prefix of the board on
 * the felt. A run whose own cards are not out yet shows the shared cards alone.
 */
export const getLiveRunBoards = (game: any): CardRef[][] | undefined => {
  if (!isLiveRunOut(game?.runs)) return undefined;
  const [total, stage, current] = String(game.runs).split(',');
  const stored = (game.multiRunCards ?? {}) as Record<string, unknown>;
  const liveCards = Array.isArray(stored[`run${current}`])
    ? (stored[`run${current}`] as CardRef[])
    : [game.card1, game.card2, game.card3, game.card4, game.card5];
  const live = dealtCards(liveCards);
  const shared = live.slice(0, sharedCardCount(stage, game.gameType));

  return Array.from({ length: Number(total) }, (_, index) => {
    const run = String(index + 1);
    if (run === current) return live;
    const raw = stored[`run${run}`];
    const tail = dealtCards(Array.isArray(raw) ? (raw as CardRef[]) : []);
    return [...shared, ...tail];
  });
};
